import i18next, { type Resource } from 'i18next';
import { bundledCatalogs, englishCatalog, type AppCatalog, type BundledLocale } from './catalogs';

const LANGUAGE_STORAGE_KEY = 'rocket-soccer.language';
const DEFAULT_LOCALE: BundledLocale = 'en';
const PLURAL_SUFFIX_PATTERN = /_(zero|one|two|few|many|other)$/;

const catalogRegistry = new Map<string, AppCatalog>(Object.entries(bundledCatalogs));

const canonicalizeLocale = (value: string): string => {
  try {
    return Intl.getCanonicalLocales(value)[0] ?? value;
  } catch {
    return value;
  }
};

const localeCandidates = (): readonly string[] => {
  const candidates: string[] = [];
  try {
    const persisted = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    if (persisted) candidates.push(persisted);
  } catch {
    // Storage may be disabled by browser privacy settings.
  }
  if (typeof navigator !== 'undefined') {
    candidates.push(...navigator.languages);
    if (navigator.language) candidates.push(navigator.language);
  }
  return candidates;
};

const resolveRegisteredLocale = (candidates: readonly string[]): string => {
  for (const candidate of candidates) {
    const canonical = canonicalizeLocale(candidate);
    if (catalogRegistry.has(canonical)) return canonical;
    const base = canonical.split('-')[0];
    if (base && catalogRegistry.has(base)) return base;
  }
  return DEFAULT_LOCALE;
};

const resources = (): Resource =>
  Object.fromEntries(
    [...catalogRegistry.entries()].map(([locale, catalog]) => [locale, structuredClone(catalog)]),
  );

for (const [locale, catalog] of catalogRegistry) assertCatalogCompleteness(locale, catalog);

void i18next.init({
  lng: resolveRegisteredLocale(localeCandidates()),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...catalogRegistry.keys()],
  defaultNS: 'common',
  resources: resources(),
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
  initAsync: false,
});

syncDocumentLanguage(i18next.resolvedLanguage ?? DEFAULT_LOCALE);

export const i18n = i18next;
export const t = i18next.t.bind(i18next);

export const activeLocale = (): string => i18next.resolvedLanguage ?? DEFAULT_LOCALE;

export const supportedLocales = (): readonly string[] => [...catalogRegistry.keys()];

export const shouldShowLanguageSelector = (): boolean => catalogRegistry.size > 1;

export const formatLocaleName = (locale: string): string => {
  const canonical = canonicalizeLocale(locale);
  const label = new Intl.DisplayNames([canonical], { type: 'language' }).of(canonical) ?? canonical;
  const first = Array.from(label)[0];
  return first ? first.toLocaleUpperCase(canonical) + label.slice(first.length) : label;
};

export function assertCatalogCompleteness(locale: string, catalog: unknown): void {
  const canonicalLocale = canonicalizeLocale(locale);
  const expectedPaths = catalogLeafPaths(englishCatalog);
  const expected = new Set(expectedPaths);
  const actual = new Set(catalogLeafPaths(catalog));
  const pluralBases = new Set<string>();
  for (const path of expectedPaths) {
    const plural = splitPluralPath(path);
    if (plural && expected.has(`${plural.base}_other`)) pluralBases.add(plural.base);
  }

  let pluralCategories: readonly Intl.LDMLPluralRule[];
  try {
    pluralCategories = new Intl.PluralRules(canonicalLocale).resolvedOptions().pluralCategories;
  } catch {
    throw new Error(`Locale catalog uses an invalid locale identifier: ${locale}.`);
  }

  const required = new Set(
    expectedPaths.filter((path) => {
      const plural = splitPluralPath(path);
      return !plural || !pluralBases.has(plural.base);
    }),
  );
  for (const base of pluralBases) {
    for (const category of pluralCategories) required.add(`${base}_${category}`);
  }

  const allowedPluralCategories = new Set<Intl.LDMLPluralRule | 'zero'>([...pluralCategories, 'zero']);
  const missing = [...required].filter((key) => !actual.has(key));
  const unexpected = [...actual].filter((key) => {
    if (required.has(key)) return false;
    const plural = splitPluralPath(key);
    return !plural || !pluralBases.has(plural.base) || !allowedPluralCategories.has(plural.suffix);
  });
  if (!missing.length && !unexpected.length) return;
  const details = [
    missing.length ? `missing: ${missing.join(', ')}` : '',
    unexpected.length ? `unexpected: ${unexpected.join(', ')}` : '',
  ].filter(Boolean).join('; ');
  throw new Error(`Locale catalog ${canonicalLocale} does not match the English catalog (${details}).`);
}

export const setLocale = async (locale: string): Promise<void> => {
  const resolved = resolveRegisteredLocale([locale]);
  await i18next.changeLanguage(resolved);
  syncDocumentLanguage(i18next.resolvedLanguage ?? resolved);
  try {
    globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, resolved);
  } catch {
    // Language changes still work when storage is unavailable.
  }
};

function syncDocumentLanguage(locale: string): void {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = locale;
  }
}

export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
): string => new Intl.NumberFormat(activeLocale(), options).format(value);

export const formatDate = (
  value: Date | number,
  options?: Intl.DateTimeFormatOptions,
): string => new Intl.DateTimeFormat(activeLocale(), options).format(value);

export const formatRelativeTime = (
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options?: Intl.RelativeTimeFormatOptions,
): string => new Intl.RelativeTimeFormat(activeLocale(), options).format(value, unit);

export const formatCurrency = (
  value: number,
  currency: string,
  options: Omit<Intl.NumberFormatOptions, 'style' | 'currency'> = {},
): string =>
  new Intl.NumberFormat(activeLocale(), {
    ...options,
    style: 'currency',
    currency,
  }).format(value);

function catalogLeafPaths(value: unknown, prefix = ''): string[] {
  if (typeof value === 'string') return prefix ? [prefix] : [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Locale catalog contains a non-string value at ${prefix || '<root>'}.`);
  }
  return Object.entries(value).flatMap(([key, child]) =>
    catalogLeafPaths(child, prefix ? `${prefix}.${key}` : key));
}

function splitPluralPath(path: string): { readonly base: string; readonly suffix: Intl.LDMLPluralRule | 'zero' } | null {
  const match = PLURAL_SUFFIX_PATTERN.exec(path);
  if (!match || !match[1]) return null;
  return {
    base: path.slice(0, -match[0].length),
    suffix: match[1] as Intl.LDMLPluralRule | 'zero',
  };
}
